import { useT } from '../lib/i18n'

interface StepProgressProps {
  /** 1-based, so it matches what the label says. */
  current: number
  total: number
}

/**
 * "Step 3 of 6", plus a track showing how far along that is.
 *
 * **The number is text, not only a bar.** A bar alone tells somebody they are
 * partway; a wizard that will not say how many questions are left is a wizard
 * people abandon, and `docs/SCIENCE.md` §6.2 is blunt about friction being the
 * thing that decides whether any of the rest matters. The bar is the redundant
 * half, not the informative one — which is also why it is `aria-hidden` and the
 * label carries the accessible name.
 *
 * Not `Bar`: that one is a nutrient bar with a visible target line, and a target
 * line means "you are 30 g past" here, which is nonsense.
 *
 * The track is a `progressbar` with an `aria-label`, and the *fill* inside it is
 * decorative. An earlier version of this comment said the track was
 * `aria-hidden` with the label carrying the name; it never was, and
 * `ui:drive:onboarding` reads `aria-valuenow` off it. Corrected at review.
 *
 * Ink rather than accent, per the design system's reservation of the coral for
 * "look here" — a progress track is context, not the thing to look at.
 *
 * SUAP-0204.
 */
export function StepProgress({ current, total }: StepProgressProps) {
  const { t } = useT()
  // Clamped so a step count the server changed under us cannot render a bar
  // wider than its track.
  const done = Math.min(Math.max(current, 0), total)
  const pct = total === 0 ? 0 : (done / total) * 100

  return (
    <div className="flex flex-col gap-2">
      <p className="text-sm tabular-nums text-ink-soft">
        {t('onboarding.progress', { current: done, total })}
      </p>
      <div
        className="h-1 w-full overflow-hidden rounded-full bg-hairline"
        role="progressbar"
        aria-label={t('onboarding.progress.label')}
        aria-valuenow={done}
        aria-valuemin={0}
        aria-valuemax={total}
      >
        <div className="h-full rounded-full bg-ink transition-[width]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}
